import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import FormField from '../../components/ui/FormField';
import {
  Input,
  Select,
  NumberInput,
  DateInput,
} from '../../components/ui/Input';
import CustomerPicker from '../../components/shared/CustomerPicker';
import ErrorBanner from '../../components/ui/ErrorBanner';
import { createSubscription } from '../../api/subscriptions';
import { extractErrorMessage } from '../../api/client';
import { BillingCycle } from '../../enums';
import { toIsoDate } from '../../utils/date';

const CYCLE_OPTIONS = [
  { value: BillingCycle.MONTHLY, label: 'Monthly' },
  { value: BillingCycle.QUARTERLY, label: 'Quarterly' },
  { value: BillingCycle.ANNUALLY, label: 'Annually' },
];

function defaultEndDate(cycle) {
  const d = new Date();
  if (cycle === BillingCycle.ANNUALLY) d.setFullYear(d.getFullYear() + 1);
  else if (cycle === BillingCycle.QUARTERLY) d.setMonth(d.getMonth() + 3);
  else d.setMonth(d.getMonth() + 1);
  return toIsoDate(d);
}

export default function SubscriptionForm({ open, onClose, onCreated }) {
  const qc = useQueryClient();
  const [customerId, setCustomerId] = useState(null);
  const [planName, setPlanName] = useState('');
  const [amount, setAmount] = useState('');
  const [billingCycle, setBillingCycle] = useState(BillingCycle.MONTHLY);
  const [startDate, setStartDate] = useState(toIsoDate(new Date()));
  const [endDate, setEndDate] = useState(defaultEndDate(BillingCycle.MONTHLY));
  const [autoRenew, setAutoRenew] = useState(true);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);

  const reset = () => {
    setCustomerId(null);
    setPlanName('');
    setAmount('');
    setBillingCycle(BillingCycle.MONTHLY);
    setStartDate(toIsoDate(new Date()));
    setEndDate(defaultEndDate(BillingCycle.MONTHLY));
    setAutoRenew(true);
    setErrors({});
    setSubmitError(null);
  };

  const handleClose = () => {
    if (mutation.isLoading) return;
    reset();
    onClose?.();
  };

  const mutation = useMutation({
    mutationFn: createSubscription,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['subscriptions'] });
      qc.invalidateQueries({ queryKey: ['revenue'] });
      toast.success(`Subscription "${data?.planName || ''}" created`);
      onCreated?.(data);
      reset();
      onClose?.();
    },
    onError: (err) => {
      setSubmitError(err);
      toast.error(extractErrorMessage(err));
    },
  });

  const validate = () => {
    const errs = {};
    if (!customerId) errs.customerId = 'Customer is required';
    if (!planName.trim()) errs.planName = 'Plan name is required';
    const amt = Number(amount);
    if (!amount || Number.isNaN(amt) || amt <= 0) {
      errs.amount = 'Amount must be greater than 0';
    }
    if (!billingCycle) errs.billingCycle = 'Billing cycle is required';
    if (!startDate) errs.startDate = 'Start date is required';
    if (!endDate) errs.endDate = 'End date is required';
    if (startDate && endDate && endDate < startDate) {
      errs.endDate = 'End date must be on or after start date';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleCycleChange = (next) => {
    setBillingCycle(next);
    // If the user hasn't customized end date too much, suggest one for the new cycle
    if (startDate) {
      const d = new Date(startDate);
      if (next === BillingCycle.ANNUALLY) d.setFullYear(d.getFullYear() + 1);
      else if (next === BillingCycle.QUARTERLY) d.setMonth(d.getMonth() + 3);
      else d.setMonth(d.getMonth() + 1);
      setEndDate(toIsoDate(d));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitError(null);
    if (!validate()) return;
    const payload = {
      customerId: Number(customerId),
      planName: planName.trim(),
      amount: Number(amount),
      billingCycle,
      startDate,
      endDate,
      autoRenew: !!autoRenew,
    };
    mutation.mutate(payload);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="New Subscription"
      description="Set up a recurring billing plan for a customer."
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={mutation.isLoading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={mutation.isLoading}
            type="submit"
          >
            Create Subscription
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {submitError && (
          <ErrorBanner error={submitError} title="Could not create subscription" />
        )}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormField
            label="Customer"
            required
            error={errors.customerId}
            className="md:col-span-2"
          >
            <CustomerPicker
              value={customerId}
              onChange={setCustomerId}
              error={!!errors.customerId}
            />
          </FormField>

          <FormField
            label="Plan Name"
            required
            error={errors.planName}
            className="md:col-span-2"
          >
            <Input
              value={planName}
              onChange={(e) => setPlanName(e.target.value)}
              placeholder="e.g. Pro Annual, Starter Monthly"
              error={!!errors.planName}
            />
          </FormField>

          <FormField label="Amount" required error={errors.amount}>
            <NumberInput
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              error={!!errors.amount}
            />
          </FormField>

          <FormField label="Billing Cycle" required error={errors.billingCycle}>
            <Select
              value={billingCycle}
              onChange={(e) => handleCycleChange(e.target.value)}
              error={!!errors.billingCycle}
            >
              {CYCLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </FormField>

          <FormField label="Start Date" required error={errors.startDate}>
            <DateInput
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              error={!!errors.startDate}
            />
          </FormField>

          <FormField label="End Date" required error={errors.endDate}>
            <DateInput
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              error={!!errors.endDate}
            />
          </FormField>

          <FormField label="Auto-Renew" className="md:col-span-2">
            <label className="inline-flex cursor-pointer select-none items-center gap-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
              <input
                type="checkbox"
                checked={autoRenew}
                onChange={(e) => setAutoRenew(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-2 focus:ring-primary/30"
              />
              <span className="text-sm text-ink">
                Automatically renew at the end of each billing cycle
              </span>
            </label>
          </FormField>
        </div>
      </form>
    </Modal>
  );
}
